import { EllipsisOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Dropdown, Menu, Rate, Tooltip } from 'antd';
import { HTMLAttributes } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import dayjs from 'lib/dayjs';
import { Between } from 'views/Conversation/Sider/TradeRefund';
import pendingSvg from '../assets/pending.svg';
import processingSvg from '../assets/processing.svg';

const Container = styled.div<{ active: string }>`
  display: flex;
  margin: 4px 7px;
  padding: 8px 8px 4px;
  border-radius: 2px;
  background: ${({ active }) => (active === 'true' ? '#F0F2F5' : 'transparent')};
  [data-color-mode='dark'] & {
    background: ${({ active }) => (active === 'true' ? 'var(--fett-bg-elevated)' : 'transparent')};
  }
  cursor: pointer;
  > span {
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    margin-right: 8px;
    border-radius: 2px;
  }
  > div {
    flex: 1 0 0;
    overflow: hidden;
  }
`;

const Ellipsis = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Name = styled(Ellipsis)`
  flex: 1 0 0;
  margin-right: 4px;
  line-height: 18px;
  font-size: 12px;
`;

const Time = styled.div`
  line-height: 18px;
  font-size: 12px;
  color: var(--fett-text-tertiary);
`;

const Content = styled(Ellipsis)`
  margin-right: 28px;
  line-height: 24px;
`;

const Status = styled.img`
  width: 16px;
  height: 16px;
`;

export const StarRate = styled(Rate)`
  font-size: 14px;
  line-height: 22px;
  > li {
    margin-right: 3px !important;
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('rate-star-zero')} div {
    color: var(--fett-border-default);
  }
`;

const Extra = styled.div`
  flex: 1 0 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  overflow: hidden;
  > * {
    flex: 0 0 auto;
    margin-left: 8px;
  }
  > div {
    flex: 1 0 0;
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--fett-text-tertiary);
  }
`;

export interface ProfileProps extends HTMLAttributes<HTMLDivElement> {
  profile: Review.Profile;
  active?: boolean;
  onStatusChange: (profile: Review.Profile, status: Review.Status) => void;
}

function Profile(props: ProfileProps) {
  const { profile, active = false, onStatusChange, ...otherProps } = props;

  const {
    userName,
    commentCreateTime,
    userPicture,
    status,
    star,
    buyerComment,
    receptionName,
    hasEdited,
  } = profile;

  const { t } = useTranslate();

  const processedStatus = [3, 4];

  const processed = processedStatus.includes(status);

  const menu = (
    <Menu>
      <Menu.Item
        onClick={(e) => {
          e.domEvent.stopPropagation();

          onStatusChange(profile, 3);
        }}
      >
        {t('domain.review.status.processed')}
      </Menu.Item>
      <Menu.Item
        onClick={(e) => {
          e.domEvent.stopPropagation();

          onStatusChange(profile, 4);
        }}
      >
        {t('domain.review.status.noActionNeeded')}
      </Menu.Item>
    </Menu>
  );

  return (
    <Dropdown disabled={processed} trigger={['contextMenu']} overlay={menu}>
      <Container active={String(active)} {...otherProps}>
        <Avatar
          shape="square"
          size={32}
          src={userPicture || undefined}
          icon={userPicture ? undefined : <UserOutlined />}
        />
        <div>
          <Between>
            <Name title={userName}>{userName}</Name>
            <Time>{dayjs(parseInt(commentCreateTime, 10)).format('YYYY-MM-DD HH:mm:ss')}</Time>
          </Between>
          {buyerComment ? <Content title={buyerComment}>{buyerComment}</Content> : null}
          <Between>
            <div>
              <StarRate allowHalf disabled count={5} value={star} />
              {hasEdited && (
                <div className="ml-[4px] px-[3px] inline-block bg-[var(--fett-bg-elevated)] text-[var(--fett-text-secondary)] text-[12px] rounded-[2px]">
                  {t('scene.reviewWorkspace.detail.section.reviewUpdate')}
                </div>
              )}
            </div>
            <Extra>
              {receptionName ? <div title={receptionName}>{receptionName}</div> : null}
              {status === 1 && (
                <Tooltip title={t('domain.review.status.pending')}>
                  <Status src={pendingSvg} alt="pending" />
                </Tooltip>
              )}
              {status === 2 && (
                <Tooltip title={t('domain.review.status.inProgress')}>
                  <Status src={processingSvg} alt="processing" />
                </Tooltip>
              )}
              {!processed && (
                <Dropdown trigger={['click']} overlay={menu}>
                  <EllipsisOutlined onClick={(e) => e.stopPropagation()} />
                </Dropdown>
              )}
            </Extra>
          </Between>
        </div>
      </Container>
    </Dropdown>
  );
}

export default Profile;
